/**
 * Absolute session lifetime (DRA-357).
 *
 * Firebase defaults to browserLocalPersistence and silently renews the ID
 * token from a long-lived refresh token, so a session otherwise lasts until
 * someone deliberately signs out. This bounds it.
 *
 * The clock is the token's `auth_time` - the last real sign-in - not the last
 * token refresh, so a session cannot extend itself just by staying open. That
 * is the same property DRA-350's step-up gate relies on.
 *
 * Kept per-app rather than in @rivermount/shared: that package's entrypoint
 * pulls in firebase-admin, and importing it from a browser bundle is what
 * DRA-343 removed.
 */

export const SESSION_MAX_AGE_MS = 4 * 60 * 60 * 1000;

/** Milliseconds since the user last actually signed in. */
export function sessionAgeMs(authTime: string | number | Date, now: number = Date.now()): number {
  const signedInAt = authTime instanceof Date ? authTime.getTime() : new Date(authTime).getTime();
  if (Number.isNaN(signedInAt)) {
    // An unreadable auth_time is not evidence of a recent sign-in, so treat it
    // as expired rather than granting an unbounded session by accident.
    return Number.POSITIVE_INFINITY;
  }
  return now - signedInAt;
}

export function isSessionExpired(
  authTime: string | number | Date,
  now: number = Date.now(),
  maxAgeMs: number = SESSION_MAX_AGE_MS
): boolean {
  return sessionAgeMs(authTime, now) >= maxAgeMs;
}

/** How long until this session expires; 0 once it already has. */
export function msUntilSessionExpiry(
  authTime: string | number | Date,
  now: number = Date.now(),
  maxAgeMs: number = SESSION_MAX_AGE_MS
): number {
  return Math.max(0, maxAgeMs - sessionAgeMs(authTime, now));
}
